import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';

const AppointmentPage = () => {
  const [appointments, setAppointments] = useState([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    const user = JSON.parse(localStorage.getItem('user') || '{}');
    const user_id = user.id;

  fetch(`${process.env.REACT_APP_VENDOR_DOMAIN}/customer_appointments?user_id=${user_id}`)
      .then(res => res.json())
      .then(data => {
        setAppointments(data.appointments || []);
        setLoading(false);
      })
      .catch(() => {
        setAppointments([]);
        setLoading(false);
      });
  }, []);

  // Fetch vendor names for each appointment
  const [vendorNames, setVendorNames] = useState({});
  useEffect(() => {
    const fetchVendorNames = async () => {
      const ids = [...new Set(appointments.map(app => app.vendor_id).filter(Boolean))];
      const names = {};
      for (const id of ids) {
        try {
          const res = await fetch(`${process.env.REACT_APP_VENDOR_DOMAIN}/vendor?vendor_id=${id}`);
          const data = await res.json();
          if (data.vendor) {
            names[id] = `${data.vendor.firstName || ''} ${data.vendor.lastName || ''}`.trim();
          }
        } catch {
          names[id] = '';
        }
      }
      setVendorNames(names);
    };
    if (appointments.length > 0) fetchVendorNames();
  }, [appointments]);

  // Fetch service detail info for each appointment
  const [serviceDetails, setServiceDetails] = useState({});
  useEffect(() => {
    const fetchServiceDetails = async () => {
      const ids = [...new Set(appointments.map(app => app.product_detail_id).filter(Boolean))];
      const details = {};
      for (const id of ids) {
        try {
          const res = await fetch(`${process.env.REACT_APP_VENDOR_DOMAIN}/api/service_details_by_id/${id}`);
          const data = await res.json();
          if (data.serviceDetail) {
            details[id] = {
              name: data.serviceDetail.name || '',
              description: data.serviceDetail.description || '',
              image_url: data.serviceDetail.image_url || '',
            };
          }
        } catch {
          details[id] = { name: '', description: '', image_url: '' };
        }
      }
      setServiceDetails(details);
    };
    if (appointments.length > 0) fetchServiceDetails();
  }, [appointments]);

  // Fetch customer ratings (comment and rating) for each appointment
  const [appointmentRatings, setAppointmentRatings] = useState({});
  useEffect(() => {
    const fetchRatings = async () => {
      const ids = appointments.map(app => app.id);
      const ratings = {};
      await Promise.all(ids.map(async (id) => {
        try {
          const res = await fetch(`${process.env.REACT_APP_VENDOR_DOMAIN}/customer_ratings/appointment/${id}`);
          const data = await res.json();
          if (data.rating) {
            ratings[id] = {
              comment: data.rating.comment || '',
              rating: data.rating.rating || null
            };
          } else {
            ratings[id] = { comment: '', rating: null };
          }
        } catch {
          ratings[id] = { comment: '', rating: null };
        }
      }));
      setAppointmentRatings(ratings);
    };
    if (appointments.length > 0) fetchRatings();
  }, [appointments]);

  // Cancel appointment handler
  const handleCancelAppointment = async (appointmentId) => {
    try {
  const res = await fetch(`${process.env.REACT_APP_VENDOR_DOMAIN}/vendor_appointments/${appointmentId}/status`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ status: 'cancelled' })
      });
      if (res.ok) {
        setAppointments(prev => prev.map(app => app.id === appointmentId ? { ...app, status: 'cancelled' } : app));
      } else {
        alert('Failed to cancel appointment.');
      }
    } catch {
      alert('Error cancelling appointment.');
    }
  };

  return (
    <div style={styles.container}>
      <h2 style={styles.title}>Your Appointments</h2>
      {loading ? (
        <div style={styles.loader}>Loading...</div>
      ) : appointments.length === 0 ? (
        <div style={styles.empty}>No appointments found.</div>
      ) : (
        <div style={styles.cardGrid}>
          {appointments
            .slice()
            .sort((a, b) => new Date(b.creation_date) - new Date(a.creation_date))
            .map(app => {
              return (
                <div
                  key={app.id}
                  style={{
                    ...styles.card,
                    background: app.status === 'pending' ? '#fff3e0' : '#f5f6fa',
                    border: app.status === 'pending' ? '2px solid #ff9800' : '2px solid #e0cfa0',
                    boxShadow: app.status === 'pending'
                      ? '0 2px 16px #ffcc80'
                      : '0 1px 8px #e0cfa0',
                    position: 'relative',
                  }}
                >
                  {app.status === 'pending' && (
                    <div style={styles.pendingBox}>Pending</div>
                  )}
                  {app.status === 'accepted' && (
                    <div style={styles.acceptedBox}>Accepted</div>
                  )}
                  {app.status === 'rejected' && (
                    <div style={styles.rejectedBox}>Rejected</div>
                  )}
                     {app.status === 'cancelled' && (
                    <div style={styles.rejectedBox}>Cancelled</div>
                  )}
                  {app.status === 'completed' && (
                    <div style={styles.completeddBox}>Completed</div>
                  )}
                  <br/>
                  <div style={styles.cardRow}>
                    <span style={styles.label}>Vendor:</span>
                    <span style={styles.value}>{vendorNames[app.vendor_id] || app.vendor_id}</span>
                  </div>
                  <div style={styles.cardRow}>
                    <span style={styles.label}>Date:</span>
                    <span style={styles.value}>
                      {app.date
                        ? (() => {
                            const d = new Date(app.date);
                            const mm = String(d.getMonth() + 1).padStart(2, '0');
                            const dd = String(d.getDate()).padStart(2, '0');
                            const yyyy = d.getFullYear();
                            return `${mm}/${dd}/${yyyy}`;
                          })()
                        : ''}
                    </span>
                  </div>
                  <div style={styles.cardRow}>
                    <span style={styles.label}>Start Time:</span>
                    <span style={styles.value}>{app.start_time}</span>
                  </div>
                  <div style={styles.cardRow}>
                    <span style={styles.label}>End Time:</span>
                    <span style={styles.value}>{app.end_time}</span>
                  </div>
                  <div style={styles.cardRow}>
                    <span style={styles.label}>Notes:</span>
                    <span style={styles.value}>{app.notes}</span>
                  </div>
                  {/* Customer Ratings Comment and Stars */}
                  {appointmentRatings[app.id] && (appointmentRatings[app.id].comment || appointmentRatings[app.id].rating) && (
                    <div style={styles.cardRow}>
                      <span style={styles.label}>My Rating:</span>
                      <span style={styles.value}>
                        {appointmentRatings[app.id].rating && (
                          <span style={{ color: '#FFD700', marginRight: '8px' }}>
                            {Array.from({ length: 5 }).map((_, i) => (
                              <span key={i}>
                                {i < appointmentRatings[app.id].rating ? '★' : '☆'}
                              </span>
                            ))}
                          </span>
                        )}
                        {appointmentRatings[app.id].comment}
                      </span>
                    </div>
                  )}
                  {app.appointment_links && (
                    <div style={styles.cardRow}>
                      <span style={styles.label}>Appointment Link:</span>
                      <span 
                        style={styles.value}
                        dangerouslySetInnerHTML={{
                          __html: app.appointment_links
                        }}
                      />
                    </div>
                  )}
                  {app.status === 'completed' || app.status === 'accepted' ? (
                    <div style={styles.buttonRow}>
                      <button
                        style={styles.ratingBtn}
                        onClick={() => navigate(`/customerratings/${app.vendor_id}/${app.product_detail_id}/${app.id}`, {
                          state: {
                            vendor_id: app.vendor_id,
                            appointment_id: app.id,
                            vendor_name: vendorNames[app.vendor_id] || 'Vendor'
                          }
                        })}
                      >
                        <span style={styles.ratingText}>Rate My Coach</span>
                      </button>
                    </div>
                  ) : null}
                  {/* Cancel button for pending appointments */}
                  {app.status === 'pending' && (
                    <div style={styles.buttonRow}>
                      <button
                        style={{
                          ...styles.ratingBtn,
                          background: '#d32f2f',
                          marginLeft: '12px',
                        }}
                        onClick={() => handleCancelAppointment(app.id)}
                      >
                        Cancel Appointment
                      </button>
                    </div>
                  )}
                </div>
              );
            })}
        </div>
      )}
      <button
        style={styles.backBtn}
        onClick={() => navigate('/customerdashboard')}
      >
        ← Back to Dashboard
      </button>
    </div>
  );
};

const styles = {
  container: {
    maxWidth: '1100px',
    margin: '48px auto',
    padding: '40px',
    background: '#fff',
    borderRadius: '22px',
    boxShadow: '0 6px 32px rgba(141,110,54,0.10)',
  },
  title: {
    color: '#4078c0',
    fontSize: '2.2rem',
    fontWeight: 'bold',
    marginBottom: '38px',
    textAlign: 'center',
    letterSpacing: '1.5px',
    fontFamily: 'Segoe UI, Arial, sans-serif',
  },
  loader: {
    textAlign: 'center',
    color: '#8d6e36',
    fontWeight: 'bold',
    fontSize: '1.2rem',
    padding: '40px 0',
  },
  empty: {
    textAlign: 'center',
    color: '#888',
    fontSize: '1.15rem',
    marginTop: '32px',
    fontStyle: 'italic',
  },
  cardGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fill, minmax(340px, 1fr))',
    gap: '32px',
    marginTop: '12px',
  },
  card: {
    background: '#f5f6fa',
    borderRadius: '18px',
    padding: '32px 26px 26px 26px',
    boxShadow: '0 1px 8px #e0cfa0',
    minHeight: '280px',
    display: 'flex',
    flexDirection: 'column',
    gap: '12px',
    border: '2px solid #e0cfa0',
    transition: 'box-shadow 0.2s, border 0.2s, background 0.2s',
    position: 'relative',
  },
  pendingBox: {
    position: 'absolute',
    top: '6px',
    right: '18px',
    background: '#ff9800',
    color: '#fff',
    fontWeight: 'bold',
    borderRadius: '8px',
    padding: '8px 22px',
    fontSize: '1.08rem',
    boxShadow: '0 1px 4px #ffcc80',
    letterSpacing: '1px',
    textAlign: 'center',
    border: '2px solid #fff3e0',
    zIndex: 2,
  },
  acceptedBox: {
    position: 'absolute',
    top: '6px',
    right: '18px',
    background: '#3b388eff',
    color: '#fff',
    fontWeight: 'bold',
    borderRadius: '8px',
    padding: '8px 22px',
    fontSize: '1.08rem',
    boxShadow: '0 1px 4px #c8e6c9',
    letterSpacing: '1px',
    textAlign: 'center',
    border: '2px solid #e0f2f1',
    zIndex: 2,
  },
   completeddBox: {
    position: 'absolute',
    top: '6px',
    right: '18px',
    background: '#718e38ff',
    color: '#fff',
    fontWeight: 'bold',
    borderRadius: '8px',
    padding: '8px 22px',
    fontSize: '1.08rem',
    boxShadow: '0 1px 4px #c8e6c9',
    letterSpacing: '1px',
    textAlign: 'center',
    border: '2px solid #e0f2f1',
    zIndex: 2,
  },
  rejectedBox: {
    position: 'absolute',
    top: '6px',
    right: '18px',
    background: '#d32f2f',
    color: '#fff',
    fontWeight: 'bold',
    borderRadius: '8px',
    padding: '8px 22px',
    fontSize: '1.08rem',
    boxShadow: '0 1px 4px #ffcdd2',
    letterSpacing: '1px',
    textAlign: 'center',
    border: '2px solid #ffebee',
    zIndex: 2,
  },
  cardRow: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '8px',
    paddingBottom: '2px',
    borderBottom: '1px solid #f0e6d2',
  },
  label: {
    fontWeight: 'bold',
    color: '#8d6e36',
    fontSize: '1.08rem',
    letterSpacing: '0.5px',
    fontFamily: 'Segoe UI, Arial, sans-serif',
  },
  value: {
    color: '#4078c0',
    fontSize: '1.08rem',
    fontWeight: 'bold',
    marginLeft: '12px',
    textAlign: 'right',
    wordBreak: 'break-word',
    fontFamily: 'Segoe UI, Arial, sans-serif',
  },
  backBtn: {
    background: '#4078c0',
    color: '#fff',
    fontWeight: 'bold',
    border: 'none',
    borderRadius: '8px',
    padding: '12px 24px',
    fontSize: '1rem',
    cursor: 'pointer',
    boxShadow: '0 1px 4px #e0cfa0',
    transition: 'background 0.2s',
    textDecoration: 'none',
    marginTop: '32px',
    display: 'block',
    marginLeft: 'auto',
    marginRight: 'auto',
  },
  buttonRow: {
    display: 'flex',
    justifyContent: 'center',
    marginTop: '16px',
    paddingTop: '12px',
    borderTop: '1px solid #f0e6d2',
  },
  ratingBtn: {
    background: '#4078c0',
    color: '#fff',
    fontWeight: 'bold',
    border: 'none',
    borderRadius: '8px',
    padding: '10px 24px',
    fontSize: '1rem',
    cursor: 'pointer',
    boxShadow: '0 1px 4px #b3c6e6',
    transition: 'background 0.2s',
  },
};

export default AppointmentPage;